/**
 * Feasibility shared constants + helpers (spec §6), reconciled against the
 * Modal repo's `feasibility/models.py`. Wrong enum values 422 on /generate, so
 * these fixed lists are the single source of truth for both the drawer's
 * Project-details capture and the report modal's top-up form.
 */

import type { ProjectBrief, ProjectBriefExtension, ProjectBriefProperty } from "@/types";

/** Fixed dropdown vocabularies (proposal values only — classification-only
 *  buckets like new_dwelling / n_a / not_an_extension are deliberately omitted). */
export const ENUMS = {
  extension_type: [
    "single_storey",
    "two_storey",
    "mixed_storey",
    "first_floor",
    "loft",
    "garage_conversion",
    "outbuilding",
    "basement",
    "porch",
  ],
  position: ["rear", "side", "front", "wrap", "multiple"],
  dwelling_type: ["house", "flat"],
  attachment: ["detached", "semi_detached", "end_terrace", "mid_terrace", "unknown"],
  // Not in models.py (a top-level /generate param) — values from the old form.
  budget_band: ["under_50k", "50k_100k", "100k_200k", "200k_plus", "unsure"],
} as const;

/** Eaves height as PD-relevant bands; the value sent is the band's UPPER edge —
 *  the figure the permitted-development check keys off (kept conservative). */
export const EAVES_BANDS = [
  { label: "Up to 2.5m", value: 2.5, hint: "typical single-storey flat/low roof" },
  { label: "2.5m – 3m", value: 3, hint: "typical single-storey pitched" },
  { label: "Over 3m", value: 3.5, hint: "two-storey, or a tall single-storey" },
] as const;

/** Overall height as GPDO bands — edges sit on the thresholds the engine checks
 *  (2.5m outbuilding-near-boundary E.1(b), 3m porch D.1(c), 4m single-storey rear/side
 *  A.1(f)/(j)); nothing above 4m is checked, so "Over 4m" sends a representative
 *  two-storey 6m (it also sizes the massing diagram). Upper edge sent, as EAVES_BANDS. */
export const HEIGHT_BANDS = [
  { label: "Up to 2.5m", value: 2.5, hint: "e.g. an outbuilding near a boundary" },
  { label: "2.5m – 3m", value: 3, hint: "typical flat-roof single-storey or porch" },
  { label: "3m – 4m", value: 4, hint: "typical pitched-roof single-storey" },
  { label: "Over 4m", value: 6, hint: "two-storey or taller" },
] as const;

/** Property-level fields /generate requires (else 422). `attachment` is optional
 *  (defaults to "unknown" engine-side and is surfaced via an /analyze follow-up). */
export const REQUIRED_PROPERTY_KEYS: (keyof ProjectBriefProperty)[] = ["dwelling_type"];

/** Per-extension fields /generate requires for every line (else 422). storeys is
 *  auto-derived for the unambiguous types but still always sent. */
export const REQUIRED_EXTENSION_KEYS: (keyof ProjectBriefExtension)[] = [
  "extension_type",
  "position",
  "depth_m",
  "width_m",
  "height_m",
  "eaves_height_m",
  "storeys",
];

/** The position value that makes an extension an L rather than a rectangle. */
export const WRAP_POSITION = "wrap";

/** The return leg — required by /generate only when the line is a wrap-around. */
export const WRAP_RETURN_KEYS: (keyof ProjectBriefExtension)[] = [
  "return_width_m",
  "return_length_m",
];

/** The type whose line is two floor plates of different sizes — the common "two-storey rear
 *  whose first floor is shallower or narrower than the ground floor". ONE line, never a
 *  single-storey line plus a first-floor line: entered as two, the engine assesses them apart
 *  and offers the ground floor a Larger Home Extension route the enlargement can't have. */
export const MIXED_STOREY_TYPE = "mixed_storey";

/** The upper floor — required by /generate only when the line is mixed-storey. */
export const MIXED_STOREY_UPPER_KEYS: (keyof ProjectBriefExtension)[] = [
  "upper_depth_m",
  "upper_width_m",
];

/** What this particular line must carry on top of the base set. A wrap-around needs its
 *  return leg measured (one width/depth pair describes a rectangle, and a wrap is an L); a
 *  mixed-storey line needs its upper floor measured (two plates, two footprints). The engine
 *  rejects either without them rather than pricing the wrong floor area. */
export function requiredExtensionKeys(
  ext: ProjectBriefExtension,
): (keyof ProjectBriefExtension)[] {
  const keys = [...REQUIRED_EXTENSION_KEYS];
  if (ext.position === WRAP_POSITION) keys.push(...WRAP_RETURN_KEYS);
  if (ext.extension_type === MIXED_STOREY_TYPE) keys.push(...MIXED_STOREY_UPPER_KEYS);
  return keys;
}

/** A fresh, empty extension line — the default first line and what "Add extension" appends. */
export function emptyExtension(): ProjectBriefExtension {
  return {};
}

/** snake_case → Title Case for labels/options. */
export function prettyLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b(\w)/g, (m) => m.toUpperCase());
}

/** Storeys auto-derive: 1 for single_storey, 2 for two_storey and mixed_storey (part of it
 *  is two storeys — the engine applies the two-storey rules to it), else manual. */
export function derivedStoreys(extensionType?: string): number | undefined {
  if (extensionType === "single_storey") return 1;
  if (extensionType === "two_storey" || extensionType === MIXED_STOREY_TYPE) return 2;
  return undefined;
}

export function storeysAutoDerived(extensionType?: string): boolean {
  return derivedStoreys(extensionType) !== undefined;
}

/** Migrate a stored brief to the property + extensions[] shape. Briefs saved
 *  before the split carry a flat `dimensions` object (property + one extension's
 *  fields combined); we split those out so the rest of the app only ever sees the
 *  new shape. Already-migrated briefs pass through; a null brief becomes an empty
 *  one with a single blank extension line (the default-to-one-line rule). */
export function normalizeBrief(brief: ProjectBrief | null | undefined): ProjectBrief {
  const b: ProjectBrief = { ...(brief ?? {}) };
  if (!b.property && !b.extensions && b.dimensions) {
    const { dwelling_type, attachment, previously_extended, original_house_age, ...ext } =
      b.dimensions;
    b.property = { dwelling_type, attachment, previously_extended, original_house_age };
    b.extensions = [ext];
  }
  b.property ??= {};
  b.extensions = b.extensions && b.extensions.length > 0 ? b.extensions : [emptyExtension()];
  delete b.dimensions; // never carry the legacy field forward
  return b;
}

/** Deep-merge a partial brief onto a base. The PATCH replaces `project_brief`
 *  wholesale, so every writer must merge against the freshest stored brief before
 *  sending — this is that merge. `property` is merged one level; `extensions` is
 *  replaced wholesale when provided (the UI owns the whole array — add/remove). */
export function mergeBrief(
  base: ProjectBrief | null | undefined,
  partial: Partial<ProjectBrief>,
): ProjectBrief {
  const merged: ProjectBrief = { ...(base ?? {}), ...partial };
  if (partial.property || base?.property) {
    merged.property = { ...(base?.property ?? {}), ...(partial.property ?? {}) };
  }
  if (partial.extensions !== undefined) merged.extensions = partial.extensions;
  // Once the brief carries the new shape, drop the legacy flat field so it can't linger stale.
  if (merged.property || merged.extensions) delete merged.dimensions;
  return merged;
}

/** Which required fields are still missing on a brief — property-level first, then
 *  per extension line (prefixed with the element number when there's more than one),
 *  so the "still needed" hint and the generate gate cover every line. */
export function missingRequired(brief: ProjectBrief | null | undefined): string[] {
  const b = normalizeBrief(brief);
  const isMissing = (v: unknown) => v === undefined || v === null || v === "";
  const out: string[] = [];
  for (const k of REQUIRED_PROPERTY_KEYS) {
    if (isMissing(b.property?.[k])) out.push(prettyLabel(k));
  }
  const exts = b.extensions ?? [];
  exts.forEach((ext, i) => {
    for (const k of requiredExtensionKeys(ext)) {
      if (isMissing(ext[k])) {
        out.push(exts.length > 1 ? `Extension ${i + 1}: ${prettyLabel(k)}` : prettyLabel(k));
      }
    }
  });
  return out;
}

/** The report web view's host. Anything else in `lead.feasibility_report_url` is a legacy Xano
 *  PDF link from before the web view, which must be left exactly as it is. */
const REPORT_HOST = "reports.arkitectly.co.uk";

/** Whether this lead's report link is a web view (rather than a legacy stored PDF). */
export function isWebReportUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  try {
    return new URL(url).hostname === REPORT_HOST;
  } catch {
    return false;
  }
}

/**
 * The staff URL for opening a report — the same link with `?preview=1`.
 *
 * Without it a staff click is indistinguishable from the client's: a real browser, no bot
 * signature, so it stamps `first_viewed_at` and bumps `view_count`. Since staff open a report to
 * check it *before* sending, every report would read as opened before the client ever saw it,
 * and "sent, not yet opened" — the most useful signal in the engagement data — would be worthless.
 *
 * This is deliberately NOT what gets stored or sent. The clean URL is the one that goes to the
 * client; see `isWebReportUrl` and the Copy link action, which exist so the two cannot be
 * confused. Sending a `?preview=1` link would silently stop counting the client's real views.
 */
export function staffPreviewUrl(url: string): string {
  if (!isWebReportUrl(url)) return url;  // legacy PDF link — leave it alone
  const u = new URL(url);
  u.searchParams.set("preview", "1");
  return u.toString();
}
